// @flow
import i18n from "i18next";
import k from "src/i18n/keys";

import { connect } from "react-redux";
import React, { useCallback } from "react";

import {
  FormControl,
  FormLabel,
  Box,
  Button,
  Input,
  FormHelperText,
} from "@chakra-ui/react";
import FieldSelect from "../FieldSelect";
import TimeInterval from "./TimeInterval";
import TimePeriod from "./TimePeriod";
import Columns from "./Columns";
import Switch from "./Switch";
import {
  setNewChartDialogAttributes,
  createChart,
  updateChart,
} from "src/actions/chart";

import type { AppState, NewChartDialog } from "src/types";

type Props = {
  newChartDialog: NewChartDialog,
  _editChart: Function,
  _createChart: Function,
  _setNewChartDialogAttributes: Function,
};

const TimeseriesBarChart = ({
  newChartDialog,
  _editChart,
  _createChart,
  _setNewChartDialogAttributes,
}: Props) => {
  const handleYaxisLabel = useCallback(
    (event: any) => {
      _setNewChartDialogAttributes({
        yAxisLabel: event.target.value,
      });
    },
    [_setNewChartDialogAttributes],
  );

  const handleNumericalField = useCallback(
    (value: ?string) => {
      _setNewChartDialogAttributes({
        computationField: value,
      });
    },
    [_setNewChartDialogAttributes],
  );

  const handleFields = useCallback(() => {
    _setNewChartDialogAttributes({
      loading: true,
    });
    if (newChartDialog.edit) {
      _editChart();
    } else {
      _createChart();
    }
  }, [_setNewChartDialogAttributes, _editChart, _createChart, newChartDialog]);

  const handleTimeInterval = useCallback(
    (event: any) => {
      _setNewChartDialogAttributes({
        timeInterval: event.target.value,
      });
    },
    [_setNewChartDialogAttributes],
  );

  return (
    <>
      <FormControl id="numericalField" mb={3}>
        <FormLabel mr="0">
          {i18n.t(k.NUMERIC_CHECKLIST_FIELD)}
          <Switch value={newChartDialog.numberFieldSwitch || false} />
        </FormLabel>
        <FormHelperText>
          {i18n.t(k.THIS_FIELD_WILL_CONTAIN_THE_NU)}
        </FormHelperText>
        <FieldSelect
          disable={!(newChartDialog.numberFieldSwitch || false)}
          value={newChartDialog.computationField}
          dependentField={newChartDialog.computationField}
          handleSelect={handleNumericalField}
          fieldType="numeric"
          numberField
        />
      </FormControl>

      <FormControl id="timeInterval" mb={3} isRequired>
        <FormLabel>{i18n.t(k.TIME_INTERVAL_FREQUENCY)}</FormLabel>
        <FormHelperText>
          {i18n.t(k.THE_INTERVAL_AT_WHICH_THE_NUME)}
        </FormHelperText>
        <TimeInterval
          value={newChartDialog.timeInterval}
          handleSelect={handleTimeInterval}
        />
      </FormControl>

      <FormControl id="timePeriod" mb={3} isRequired>
        <FormLabel>{i18n.t(k.TIME_PERIOD)}</FormLabel>
        <FormHelperText>
          {i18n.t(k.SELECT_A_TIME_FRAME_OR_A_DATE)}
        </FormHelperText>
        <TimePeriod
          timePeriod={newChartDialog.timeDiff}
          timeInterval={newChartDialog.timeInterval}
        />
      </FormControl>

      <FormControl id="yAxisLabel" mb={3}>
        <FormLabel>{i18n.t(k.Y_AXIS_LABEL)}</FormLabel>
        <FormHelperText>
          {i18n.t(k.LABEL_FOR_Y_AXIS_OF_THE_CHART)}
        </FormHelperText>
        <Input
          type="text"
          value={newChartDialog.yAxisLabel}
          onChange={handleYaxisLabel}
          placeholder={i18n.t(k.ENTER_LABEL)}
          size="sm"
        />
      </FormControl>

      <FormControl id="column" mb={3} isRequired>
        <FormLabel>{i18n.t(k.COLUMN1)}</FormLabel>
        <FormHelperText>
          {i18n.t(k.SHOW_A_COLUMN_FOR_EACH_OF_THE)}
        </FormHelperText>
        <Columns value={newChartDialog.columns} />
      </FormControl>

      <Box mb={3} mt={6}>
        {newChartDialog.edit ? (
          <Button
            onClick={handleFields}
            disabled={
              newChartDialog.title.length === 0 ||
              !(
                newChartDialog.columns?.created ||
                newChartDialog.columns?.completed
              ) ||
              newChartDialog.timeDiff == null
            }
            type="button"
            isLoading={newChartDialog.loading}
            size="sm"
            colorScheme="blue"
          >
            {i18n.t(k.SAVE_CHANGES1)}
          </Button>
        ) : (
          <Button
            onClick={handleFields}
            disabled={
              newChartDialog.title.length === 0 ||
              !(
                newChartDialog.columns?.created ||
                newChartDialog.columns?.completed
              ) ||
              newChartDialog.timeDiff === null
            }
            type="button"
            isLoading={newChartDialog.loading}
            size="sm"
            colorScheme="blue"
          >
            {i18n.t(k.CREATE_AND_SAVE)}
          </Button>
        )}
      </Box>
    </>
  );
};

const mapStateToProps = ({ app }: { app: AppState }) => ({
  newChartDialog: app.charts.newChartDialog,
});

export default connect(mapStateToProps, {
  _setNewChartDialogAttributes: setNewChartDialogAttributes,
  _createChart: createChart,
  _editChart: updateChart,
})(TimeseriesBarChart);
